import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    fetchBaseQuery,
} from '@reduxjs/toolkit/query'
import { CollectionResult, Query } from './interfaces'
import { TokenModel, requestMsalAccessToken } from '@services/auth'
import { logout, storeToken } from '@store/auth'

import { Mutex } from 'async-mutex'
import { getAppConfig } from '@utils/globals'
import { getMsalInstance } from '@services/auth/msal.service'
import { setMainNavigate } from '@store/main'

export const QueryFilterOperator = Object.freeze({
    Equals: '==',
    NotEquals: '!=',
    Contains: '@=',
    NotContains: '!@=',
    StartsWith: '_=',
    NotStartsWith: '!_=',
    EndsWith: '_-=',
    NotEndsWith: '!_-=',
    GreaterThan: '>',
    GreaterThanOrEqual: '>=',
    LessThan: '<',
    LessThanOrEqual: '<=',
})

const mutex = new Mutex()
const baseQuery = fetchBaseQuery({
    baseUrl: getAppConfig().apiUrl,
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as any).auth.user?.token
        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
        }
        return headers
    },
})

export const globalBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    await mutex.waitForUnlock()

    let result = await baseQuery(args, api, extraOptions)
    if (result.error?.status !== 401) return result

    if (mutex.isLocked()) {
        await mutex.waitForUnlock()
        return await baseQuery(args, api, extraOptions)
    }

    const isAzureToken = (api.getState() as any)['auth']?.user?.isAzureToken
    const msalInstance = getMsalInstance()

    // Handle Azure user's refresh token
    if (isAzureToken && msalInstance) {
        const release = await mutex.acquire()

        try {
            await msalInstance?.initialize()
            const authResult = await requestMsalAccessToken(msalInstance)

            if (authResult && authResult.accessToken) {
                const newToken = {
                    ...((api.getState() as any)['auth']?.user as TokenModel),
                }
                newToken.token = authResult.accessToken
                newToken.isAzureToken = true

                await api.dispatch(storeToken(newToken))

                result = await baseQuery(args, api, extraOptions)
            }
        } finally {
            release()
        }

        return result
    }

    // Handle local user's refresh token
    const release = await mutex.acquire()
    try {
        if (await handleRefreshToken(args, api, extraOptions)) {
            result = await baseQuery(args, api, extraOptions)
        }
    } finally {
        release()
    }

    return result
}

export const buildQueryParams = (
    query: Query,
    includes: string[] = [],
    extras: any = null
) => {
    const params = new URLSearchParams()

    if (!query) return params

    if (query.page) {
        params.set('page', query.page.toString())
    }

    if (query.pageSize) {
        params.set('pageSize', query.pageSize.toString())
    }

    if (query.sorts) {
        params.set('sorts', query.sorts.join(','))
    }

    if (includes && includes.length > 0) {
        includes.forEach((include, index) => {
            params.set(`includes[${index}]`, include)
        })
    }

    if (query.filters && query.filters.length > 0) {
        params.set('filters', query.filters.join(','))
    }

    if (query.nextLink) params.set('nextLink', query.nextLink)

    if (extras) {
        Object.keys(extras).forEach((key) => {
            params.set(key, extras[key])
        })
    }

    return params
}

/**
 * Build a search query.
 * @param search Search phrase to be used
 * @param fields Fields to search on
 * @param operator Operator to use in the search request (default: Contains)
 * @param sortBy Sort by field and direction
 * @returns Search query that can be used in the API request
 */
export const buildSearchQuery = (
    search: string,
    fields: string[],
    operator: keyof typeof QueryFilterOperator = 'Contains',
    sortBy:
        | { field: string; direction: 'asc' | 'desc' }
        | undefined = undefined,
    page: number = 1,
    pageSize: number = 10
): Query => {
    const searchField = fields.length > 1 ? `(${fields.join('|')})` : fields[0]
    const sort = sortBy
        ? `${sortBy.direction === 'desc' ? '-' : ''}${sortBy.field}`
        : undefined

    return {
        filters:
            searchField && search
                ? [`${searchField}${QueryFilterOperator[operator]}${search}`]
                : undefined,
        sorts: sort ? [sort] : undefined,
        page,
        pageSize,
    }
}

export const objectToQueryParams = (
    obj: Record<string, any>
): URLSearchParams => {
    const params = new URLSearchParams()

    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key]
            // Skip undefined and null values
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    // Handle arrays by adding multiple key=value pairs
                    value.forEach((item) => {
                        if (item !== undefined && item !== null)
                            params.append(key, item)
                    })
                } else {
                    if (value !== undefined && value !== null)
                        params.append(key, value)
                }
            }
        }
    }

    return params
}

export const mergeURLSearchParams = (
    params1: URLSearchParams,
    params2: URLSearchParams
): URLSearchParams => {
    const mergedParams = new URLSearchParams()

    // Append all entries from params1 to mergedParams
    params1.forEach((value, key) => {
        mergedParams.append(key, value)
    })

    // Append all entries from params2 to mergedParams
    params2.forEach((value, key) => {
        mergedParams.append(key, value)
    })

    return mergedParams
}

/**
 * Build a search query with type.
 * @param search Search phrase to be used
 * @param fields Fields to search on
 * @param operator Operator to use in the search request (default: Contains)
 * @param sortBy Sort by field and direction
 * @returns Search query that can be used in the API request
 */
export const buildSearchQueryWithType = <T>(
    search: string,
    fields: (keyof T)[],
    operator: keyof typeof QueryFilterOperator = 'Contains',
    sortBy:
        | { field: keyof T; direction: 'asc' | 'desc' }
        | undefined = undefined,
    page: number = 1,
    pageSize: number = 10
): Query =>
    buildSearchQuery(
        search,
        fields as string[],
        operator,
        sortBy as { field: string; direction: 'asc' | 'desc' },
        page,
        pageSize
    )

/**
 * Default page size options.
 * @type {number[]}
 */
export const DEFAULT_PAGE_SIZE_OPTS: number[] = [25, 50, 100]
export const DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE_OPTS[1] // 50 rows per page

export const getDefaultQuery = (
    sortField?: string,
    sortDir?: 'asc' | 'desc'
): Query => ({
    pageSize: DEFAULT_PAGE_SIZE,
    sorts: sortField ? [`${sortDir === 'desc' ? '-' : ''}${sortField}`] : [],
})

/**
 * Handles refreshing the token.
 * @returns boolean indicating if the token was refreshed.
 */
const handleRefreshToken = async (args: any, api: any, extraOptions: any) => {
    const user = (api.getState() as any)['auth']?.user
    if (user) {
        const newToken = await baseQuery(
            {
                url: 'account/refresh-token',
                method: 'POST',
                body: { token: user.token, refreshToken: user.refreshToken },
            },
            api,
            extraOptions
        )
        if (newToken.data) {
            api.dispatch(storeToken(newToken.data as TokenModel))
            return true
        }
    }

    api.dispatch(logout())
    api.dispatch(setMainNavigate('/auth/login'))
    return false
}

export const CACHE_TAG_TYPES = [
    'Tenant',
    'Agent',
    'Device',
    'Group',
    'Sensor',
    'User',
    'Role',
    'NetworkGroup',
    'Option',
    'Alarm',
    'AppInfo',
    'NodeStatus',
]

export const provideCacheTag = <T>(
    result: T | CollectionResult<T>,
    type: (typeof CACHE_TAG_TYPES)[number]
) => {
    // check if the result is a collection
    if (result && (result as CollectionResult<T>).items) {
        return (result as CollectionResult<T>).items.map((item) => ({
            type,
            id: (item as any).id,
        }))
    }

    // otherwise, it's a single item
    if (result) {
        return [{ type, id: (result as any).id }]
    }

    return [type]
}

export const invalidateCacheTag = (
    type: (typeof CACHE_TAG_TYPES)[number],
    id?: any | any[]
): any[] => {
    if (id && !Array.isArray(id)) {
        return [{ type, id }]
    }

    if (id && Array.isArray(id)) {
        return id.map((i) => ({ type, id: i }))
    }

    return [type]
}
